// src/components/modules/Dialog/OTPVerificationDialog.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Button,
  Typography,
  Space,
  Input,
  message,
  Spin,
  Alert,
  Radio,
  Descriptions,
  Divider,
  Card,
  Tag,
} from "antd";
import {
  SafetyOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  MobileOutlined,
  UserOutlined,
  IdcardOutlined,
  TeamOutlined,
  SendOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import api from "../../../axiosConfig";

const { Text } = Typography;

/**
 * Componente para verificación OTP en cierre de traslados TAI y normales
 * Flujo: 1) Cargar y seleccionar firmante -> 2) Enviar OTP -> 3) Verificar código
 */
const OTPVerificationDialog = ({
  open,
  onClose,
  onVerified,
  trasladoId,
  mensaje,
  tipo = "tai",
  estadoCierre = "Finalizado",
}) => {
  // Estados del flujo
  const [paso, setPaso] = useState(1); // 1: Seleccionar firmante, 2: Verificar OTP
  const [firmantes, setFirmantes] = useState([]);
  const [selectedFirmanteIndex, setSelectedFirmanteIndex] = useState(null);
  const [loadingFirmantes, setLoadingFirmantes] = useState(false);

  // Estados de OTP
  const [loading, setLoading] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [code, setCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(300);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState("");
  const [attemptsInfo, setAttemptsInfo] = useState("");

  // Refs
  const timerRef = useRef(null);
  const cooldownRef = useRef(null);
  const inputRef = useRef(null);

  // Limpiar estados al cerrar
  const resetState = useCallback(() => {
    setPaso(1);
    setFirmantes([]);
    setSelectedFirmanteIndex(null);
    setLoadingFirmantes(false);
    setLoading(false);
    setSendingOTP(false);
    setOtpSent(false);
    setCode("");
    setPhoneNumber("");
    setExpiresAt(null);
    setTimeRemaining(300);
    setResendCooldown(0);
    setError("");
    setAttemptsInfo("");
    if (timerRef.current) clearInterval(timerRef.current);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
  }, []);

  // Cargar firmantes del traslado
  const cargarFirmantes = useCallback(async () => {
    if (!trasladoId) return;

    setLoadingFirmantes(true);
    setError("");

    try {
      const response = await api.post("/otp/firmantes", {
        traslado_id: trasladoId,
      });

      if (response.data.success) {
        setFirmantes(response.data.data.firmantes || []);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Error al cargar firmantes";
      setError(errorMsg);
    } finally {
      setLoadingFirmantes(false);
    }
  }, [trasladoId]);

  // Cargar firmantes al abrir
  useEffect(() => {
    if (open && trasladoId) {
      cargarFirmantes();
    }
  }, [open, trasladoId, cargarFirmantes]);

  // Obtener firmante seleccionado
  const firmanteSeleccionado = selectedFirmanteIndex !== null ? firmantes[selectedFirmanteIndex] : null;

  // Manejar selección de firmante
  const handleFirmanteChange = (e) => {
    setSelectedFirmanteIndex(e.target.value);
    setError("");
  };

  // Enviar OTP
  const sendOTP = useCallback(async () => {
    if (!trasladoId || !firmanteSeleccionado) return;

    setSendingOTP(true);
    setError("");

    try {
      const response = await api.post("/otp/send", {
        traslado_id: trasladoId,
        tipo: tipo,
        telefono_firmante: firmanteSeleccionado.telefono || null,
      });

      if (response.data.success) {
        setOtpSent(true);
        setPaso(2);
        setPhoneNumber(response.data.data.phoneNumber);
        setExpiresAt(response.data.data.expiresAt);

        const expires = new Date(response.data.data.expiresAt);
        const now = new Date();
        const remaining = Math.max(0, Math.floor((expires - now) / 1000));
        setTimeRemaining(remaining);
        setResendCooldown(60);

        message.success("Código de verificación enviado");

        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Error al enviar código OTP";
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setSendingOTP(false);
    }
  }, [trasladoId, tipo, firmanteSeleccionado]);

  // Continuar al paso 2 (enviar OTP)
  const handleContinuarAOTP = () => {
    if (selectedFirmanteIndex === null) {
      setError("Seleccione quién confirma el traslado");
      return;
    }
    if (!firmanteSeleccionado || firmanteSeleccionado.nombre === "-") {
      setError("No hay datos del firmante seleccionado");
      return;
    }
    if (!firmanteSeleccionado.telefono) {
      setError("El firmante seleccionado no tiene número de teléfono registrado");
      return;
    }
    sendOTP();
  };

  // Verificar OTP
  const verifyOTP = useCallback(async () => {
    if (!code || code.length !== 6) {
      setError("Ingresa el código de 6 dígitos");
      return;
    }

    if (!firmanteSeleccionado) {
      setError("No hay datos del firmante");
      return;
    }

    setLoading(true);
    setError("");
    setAttemptsInfo("");

    try {
      const response = await api.post("/otp/verify", {
        traslado_id: trasladoId,
        code: code,
        estado_cierre: estadoCierre,
        mensaje: mensaje,
        tipo: tipo,
        telefono_firmante: firmanteSeleccionado.telefono || null,
        firmante: {
          tipo_firmante: firmanteSeleccionado.tipo,
          nombre: firmanteSeleccionado.nombre,
          tipo_documento: firmanteSeleccionado.tipo_documento,
          numero_documento: firmanteSeleccionado.numero_documento,
          parentesco: firmanteSeleccionado.parentesco,
        },
      });

      if (response.data.success && response.data.verified) {
        message.success("Traslado cerrado exitosamente");
        onVerified(response.data.data);
        resetState();
        onClose();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Error al verificar código";
      setError(errorMsg);

      if (errorMsg.includes("Intentos restantes")) {
        setAttemptsInfo(errorMsg);
      }

      if (errorMsg.includes("expirado") || errorMsg.includes("Maximo de intentos")) {
        setOtpSent(false);
        setCode("");
      }
    } finally {
      setLoading(false);
    }
  }, [code, trasladoId, mensaje, tipo, estadoCierre, firmanteSeleccionado, onVerified, onClose, resetState]);

  // Reenviar OTP
  const resendOTP = useCallback(async () => {
    if (resendCooldown > 0) return;

    setSendingOTP(true);
    setError("");
    setCode("");

    try {
      const response = await api.post("/otp/resend", {
        traslado_id: trasladoId,
        telefono_firmante: firmanteSeleccionado?.telefono || null,
      });

      if (response.data.success) {
        setOtpSent(true);
        setPhoneNumber(response.data.data.phoneNumber);
        setExpiresAt(response.data.data.expiresAt);

        const expires = new Date(response.data.data.expiresAt);
        const now = new Date();
        const remaining = Math.max(0, Math.floor((expires - now) / 1000));
        setTimeRemaining(remaining);
        setResendCooldown(60);

        message.success("Código reenviado exitosamente");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Error al reenviar código";
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setSendingOTP(false);
    }
  }, [trasladoId, resendCooldown, firmanteSeleccionado]);

  // Timer para countdown de expiración
  useEffect(() => {
    if (otpSent && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setError("El código ha expirado. Solicita uno nuevo.");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [otpSent, expiresAt]);

  // Timer para cooldown de reenvío
  useEffect(() => {
    if (resendCooldown > 0) {
      cooldownRef.current = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(cooldownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [resendCooldown]);

  // Limpiar al cerrar
  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  // Formatear tiempo restante
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Manejar cambio en el input
  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(value);
    setError("");
    setAttemptsInfo("");
  };

  // Manejar Enter en el input
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && code.length === 6) {
      verifyOTP();
    }
  };

  const handleCancel = () => {
    resetState();
    onClose();
  };

  // Volver al paso 1
  const handleVolver = () => {
    setPaso(1);
    setOtpSent(false);
    setCode("");
    setError("");
    if (timerRef.current) clearInterval(timerRef.current);
  };

  // Separar paciente y acompañantes
  const paciente = firmantes.find(f => f.tipo === "paciente");
  const acompanantes = firmantes.filter(f => f.tipo === "acompanante");

  return (
    <Modal
      open={open}
      title={
        <Space>
          <SafetyOutlined style={{ color: "#722ed1", fontSize: "20px" }} />
          <Text strong style={{ color: "#2E3B4E", fontSize: "16px" }}>
            {paso === 1 ? "Seleccionar Firmante" : "Verificación de Cierre"}
          </Text>
        </Space>
      }
      onCancel={handleCancel}
      footer={null}
      centered
      width={550}
      maskClosable={false}
      closable={!loading && !sendingOTP}
    >
      <div style={{ padding: "16px 0" }}>
        {/* PASO 1: Seleccionar quién firma */}
        {paso === 1 && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {loadingFirmantes ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <Spin size="large" />
                <Text style={{ display: "block", marginTop: 16, color: "#666" }}>
                  Cargando firmantes...
                </Text>
              </div>
            ) : (
              <>
                <Alert
                  type="info"
                  showIcon
                  icon={<UserOutlined />}
                  message="Seleccione quién confirma el traslado"
                  description="El código de verificación se enviará al teléfono del firmante seleccionado."
                />

                <div style={{ marginTop: 16 }}>
                  <Text strong style={{ display: "block", marginBottom: 12 }}>
                    ¿Quién confirma y valida este servicio?
                  </Text>

                  <Radio.Group
                    onChange={handleFirmanteChange}
                    value={selectedFirmanteIndex}
                    style={{ width: "100%" }}
                  >
                    <Space direction="vertical" style={{ width: "100%" }}>
                      {/* Opción Paciente */}
                      {paciente && (
                        <Card
                          size="small"
                          hoverable
                          style={{
                            borderColor: selectedFirmanteIndex === 0 ? "#722ed1" : "#d9d9d9",
                            backgroundColor: selectedFirmanteIndex === 0 ? "#f9f0ff" : "#fff",
                            opacity: paciente.telefono ? 1 : 0.6,
                          }}
                          onClick={() => handleFirmanteChange({ target: { value: 0 } })}
                        >
                          <Radio value={0} style={{ width: "100%" }}>
                            <Space>
                              <UserOutlined style={{ color: "#722ed1" }} />
                              <Text strong>Paciente</Text>
                              {!paciente.telefono && (
                                <Tag color="red" style={{ marginLeft: 8 }}>Sin teléfono</Tag>
                              )}
                            </Space>
                          </Radio>
                          <div style={{ marginLeft: 24, marginTop: 8 }}>
                            <Descriptions size="small" column={1} style={{ marginBottom: 0 }}>
                              <Descriptions.Item label="Nombre">
                                {paciente.nombre || "-"}
                              </Descriptions.Item>
                              <Descriptions.Item label="Documento">
                                {paciente.tipo_documento} {paciente.numero_documento || "-"}
                              </Descriptions.Item>
                              <Descriptions.Item label={<><MobileOutlined /> Teléfono</>}>
                                {paciente.telefono || <Text type="danger">No registrado</Text>}
                              </Descriptions.Item>
                            </Descriptions>
                          </div>
                        </Card>
                      )}

                      {/* Opciones de Acompañantes */}
                      {acompanantes.length > 0 ? (
                        acompanantes.map((acompanante, idx) => {
                          const realIndex = idx + 1; // +1 porque el paciente es índice 0
                          return (
                            <Card
                              key={realIndex}
                              size="small"
                              hoverable
                              style={{
                                borderColor: selectedFirmanteIndex === realIndex ? "#722ed1" : "#d9d9d9",
                                backgroundColor: selectedFirmanteIndex === realIndex ? "#f9f0ff" : "#fff",
                                opacity: acompanante.telefono ? 1 : 0.6,
                              }}
                              onClick={() => handleFirmanteChange({ target: { value: realIndex } })}
                            >
                              <Radio value={realIndex} style={{ width: "100%" }}>
                                <Space>
                                  <TeamOutlined style={{ color: "#722ed1" }} />
                                  <Text strong>
                                    {acompanante.parentesco || "Acompañante"}
                                  </Text>
                                  {acompanante.es_principal && (
                                    <Tag color="gold" icon={<CrownOutlined />} style={{ marginLeft: 8 }}>
                                      Principal
                                    </Tag>
                                  )}
                                  {!acompanante.telefono && (
                                    <Tag color="red" style={{ marginLeft: 8 }}>Sin teléfono</Tag>
                                  )}
                                </Space>
                              </Radio>
                              <div style={{ marginLeft: 24, marginTop: 8 }}>
                                <Descriptions size="small" column={1} style={{ marginBottom: 0 }}>
                                  <Descriptions.Item label="Nombre">
                                    {acompanante.nombre || "-"}
                                  </Descriptions.Item>
                                  <Descriptions.Item label="Documento">
                                    {acompanante.tipo_documento} {acompanante.numero_documento || "-"}
                                  </Descriptions.Item>
                                  <Descriptions.Item label={<><MobileOutlined /> Teléfono</>}>
                                    {acompanante.telefono || <Text type="danger">No registrado</Text>}
                                  </Descriptions.Item>
                                </Descriptions>
                              </div>
                            </Card>
                          );
                        })
                      ) : (
                        <Card
                          size="small"
                          style={{
                            borderColor: "#d9d9d9",
                            backgroundColor: "#fafafa",
                            opacity: 0.6,
                          }}
                        >
                          <Space>
                            <TeamOutlined style={{ color: "#999" }} />
                            <Text style={{ color: "#999" }}>
                              No hay acompañantes registrados para este paciente
                            </Text>
                          </Space>
                        </Card>
                      )}
                    </Space>
                  </Radio.Group>
                </div>

                {/* Errores */}
                {error && (
                  <Alert type="error" message={error} showIcon style={{ marginTop: 8 }} />
                )}

                {/* Botones */}
                <Space style={{ width: "100%", justifyContent: "space-between", marginTop: 16 }}>
                  <Button onClick={handleCancel}>
                    Cancelar
                  </Button>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleContinuarAOTP}
                    loading={sendingOTP}
                    disabled={selectedFirmanteIndex === null}
                    style={{ backgroundColor: "#722ed1", borderColor: "#722ed1" }}
                  >
                    Enviar Código OTP
                  </Button>
                </Space>
              </>
            )}
          </Space>
        )}

        {/* PASO 2: Verificar OTP */}
        {paso === 2 && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {/* Información del envío */}
            <Alert
              type="info"
              showIcon
              icon={<MobileOutlined />}
              message={
                <span>
                  Código enviado al número: <strong>{phoneNumber}</strong>
                </span>
              }
            />

            {/* Datos del firmante (solo lectura) */}
            <Card size="small" style={{ backgroundColor: "#f9f0ff", borderColor: "#d3adf7" }}>
              <Space style={{ marginBottom: 8 }}>
                {firmanteSeleccionado?.tipo === "paciente" ? (
                  <UserOutlined style={{ color: "#722ed1" }} />
                ) : (
                  <TeamOutlined style={{ color: "#722ed1" }} />
                )}
                <Text strong style={{ color: "#722ed1" }}>
                  {firmanteSeleccionado?.tipo === "paciente"
                    ? "Paciente"
                    : firmanteSeleccionado?.parentesco || "Acompañante"}
                </Text>
                {firmanteSeleccionado?.es_principal && (
                  <Tag color="gold" icon={<CrownOutlined />}>Principal</Tag>
                )}
              </Space>
              <Descriptions size="small" column={1} style={{ marginBottom: 0 }}>
                <Descriptions.Item label={<><IdcardOutlined /> Nombre</>}>
                  {firmanteSeleccionado?.nombre || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Documento">
                  {firmanteSeleccionado?.tipo_documento} {firmanteSeleccionado?.numero_documento || "-"}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Divider style={{ margin: "8px 0" }} />

            {/* Input del código */}
            <div style={{ textAlign: "center" }}>
              <Text strong style={{ display: "block", marginBottom: 12 }}>
                Ingresa el código de 6 dígitos:
              </Text>
              <Input
                ref={inputRef}
                value={code}
                onChange={handleCodeChange}
                onKeyPress={handleKeyPress}
                maxLength={6}
                style={{
                  width: "200px",
                  textAlign: "center",
                  fontSize: "24px",
                  letterSpacing: "8px",
                  fontWeight: "bold",
                }}
                placeholder="------"
                disabled={loading || timeRemaining === 0}
              />
            </div>

            {/* Tiempo restante */}
            <div style={{ textAlign: "center" }}>
              <Space>
                <ClockCircleOutlined
                  style={{ color: timeRemaining < 60 ? "#ff4d4f" : "#722ed1" }}
                />
                <Text style={{ color: timeRemaining < 60 ? "#ff4d4f" : "#666" }}>
                  Expira en: <strong>{formatTime(timeRemaining)}</strong>
                </Text>
              </Space>
            </div>

            {/* Errores */}
            {error && (
              <Alert type="error" message={error} showIcon style={{ marginTop: 8 }} />
            )}

            {attemptsInfo && !error.includes(attemptsInfo) && (
              <Alert type="warning" message={attemptsInfo} showIcon style={{ marginTop: 8 }} />
            )}

            {/* Reenviar código */}
            <div style={{ textAlign: "center" }}>
              <Text type="secondary">¿No recibiste el código?</Text>
              <br />
              <Button
                type="link"
                icon={<ReloadOutlined />}
                onClick={resendOTP}
                disabled={resendCooldown > 0 || sendingOTP}
                loading={sendingOTP}
              >
                {resendCooldown > 0
                  ? `Reenviar código (${resendCooldown}s)`
                  : "Reenviar código"}
              </Button>
            </div>

            {/* Botones de acción */}
            <Space style={{ width: "100%", justifyContent: "space-between", marginTop: 16 }}>
              <Button onClick={handleVolver} disabled={loading}>
                Volver
              </Button>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={verifyOTP}
                loading={loading}
                disabled={code.length !== 6 || timeRemaining === 0}
                style={{ backgroundColor: "#722ed1", borderColor: "#722ed1" }}
              >
                Verificar y Cerrar
              </Button>
            </Space>
          </Space>
        )}
      </div>
    </Modal>
  );
};

OTPVerificationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onVerified: PropTypes.func.isRequired,
  trasladoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mensaje: PropTypes.string,
  estadoCierre: PropTypes.oneOf(["Finalizado", "Fallido"]),
  tipo: PropTypes.oneOf(["tai", "normal", "validacion"]),
};

OTPVerificationDialog.defaultProps = {
  tipo: "tai",
};

export default OTPVerificationDialog;
